import React, { useState, useEffect } from 'react';
import { Modal, Button, Divider } from 'antd';
import PropTypes from 'prop-types';

import { CloseIcon, SectionAddIcon, UploadIcon } from 'components/Icons';
import { GalleryImageEditor } from './components';
import UploadContentLibraryItem from 'components/UploadContentLibraryItem/UploadContentLibraryItem';
import Popconfirm from 'components/PopConfirm';
import utils from 'utils/utils';

import './GalleryEditModal.scss';

const DEFAULT_STATE = { images: [], config: {} };

/**
 * GalleryEditModal
 * @param {Object} visible - The visible state
 * @param {Function} onCancel - The function to cancel the modal
 * @param {Function} onConfirm - The function to confirm the modal
 * @param {Object} galleryEdit - The gallery edit
 * @param {Object} prop - The proposal
 * @param {boolean} savingRichEditorModal - Whether the rich editor is saving
 * @param {boolean} isWix - Whether the editor is in Wix mode
 */
const GalleryEditModal = ({
  visible,
  onCancel,
  onConfirm,
  galleryEdit,
  prop,
  savingRichEditorModal,
  isWix,
}) => {
  const containerWidth = galleryEdit.config.containerWidth || 860;

  const [selectImage, setSelectImage] = useState(!galleryEdit?.v.length ? true : false);
  const [removeItems, setRemoveItems] = useState(false);
  const [moreButtons, setMoreButtons] = useState(false);
  const [showPropModal, setShowPropModal] = useState(visible);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [temporary, setTemporary] = useState({ ...DEFAULT_STATE });
  const [showBack, setShowBack] = useState(false);
  const [cancelModal, setCancelModal] = useState(null);

  /**
   * Handle the key command
   * @param {Object} e - The event
   * @returns {void}
   */
  useEffect(() => {
    if (!visible) {
      setTemporary({ ...DEFAULT_STATE });
    } else {
      setTemporary({
        images: [...galleryEdit?.v],
        config: { ...galleryEdit?.config },
      });
    }
  }, [visible, galleryEdit]);

  /**
   * Handle the key command
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleKeyCommand = (e) => {
    if (e.key === 'Escape') {
      setShowModal(true);
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        makeEscapeModal(true);
      }
    }
  };

  /**
   * Make the escape modal
   * @param {boolean} showModal - Whether to show the modal
   * @returns {void}
   */
  const makeEscapeModal = (showModal) => {
    setShowPromptModal(showModal);
    setShowModal(showModal);
  };

  /**
   * Handle the key command
   * @returns {void}
   */
  useEffect(() => {
    if (!showPromptModal) {
      window.addEventListener('keydown', handleKeyCommand, true);
    } else {
      window.removeEventListener('keydown', handleKeyCommand, true);
    }
    setShowPromptModal(!showPromptModal); // eslint-disable-next-line
  }, []);

  /**
   * Load the image
   * @param {string} url - The URL of the image
   * @returns {Promise} - Promise that resolves when the image is loaded
   */
  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function () {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = function () {
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  /**
   * Handle the layout change
   * @param {Array} newItems - The new items
   * @returns {void}
   */
  const onLayoutChange = (newItems) => {
    setRemoveItems(true);
    setTemporary((prev) => ({ ...prev, images: [...newItems] }));
  };

  /**
   * Handle the config change
   * @param {Object} newConfig - The new config
   * @returns {void}
   */
  const onConfigChange = (newConfig) => {
    setTemporary((prev) => ({ ...prev, config: newConfig }));
  };

  /**
   * Handle the save
   * @returns {void}
   */
  const handleSave = () => {
    if (selectImage) {
      setSelectImage(false);
      setTemporary((prev) => {
        const newData = { ...prev, images: prev.tempImages };
        delete newData.tempImages;
        return newData;
      });
    } else {
      setTemporary({ ...DEFAULT_STATE });
      onConfirm(temporary);
    }
  };

  /**
   * Handle the upload image modal close
   * @returns {void}
   */
  const onUploadImageModalClose = () => {
    // remove all uploaded & temp images
    setTemporary((prev) => {
      const newData = { ...prev };
      delete newData.tempImages;
      return newData;
    });
  };

  /**
   * Get the modal props
   * @returns {Object} - The modal props
   */
  const modalProps = {
    centered: true,
    visible: showPropModal,
    closeIcon: <CloseIcon />,
    onCancel: () => setShowModal(true),
    cancelButtonProps: { disabled: savingRichEditorModal },
    maskClosable: !savingRichEditorModal,
    closable: !savingRichEditorModal,
    onOk: onConfirm,
    wrapClassName: `gallery-modal ${selectImage ? 'select-box' : 'edit-gallery'}`,
  };

  /**
   * Get the modal title
   * @param {string} title - The title
   * @returns {React.ReactNode} - The modal title
   */
  const modalTitle = (title) => (
    <div className="rich-editor-price-edit-header">
      <h3 className="title">
        {showBack && (
          <Button className="medium-btn grey-btn back-btn" onClick={() => setShowBack(false)}>
            Back
          </Button>
        )}
        <span>{title}</span>
      </h3>
      <Divider />
    </div>
  );

  /**
   * Get the modal footer
   * @param {Object} props - The props
   * @returns {React.ReactNode} - The modal footer
   */
  const modalFooter = ({ onCancel, disabled, btnText = 'ADD TO GALLERY' }) => [
    <Button key="cancel" className="large-btn grey-btn" onClick={onCancel}>
      CANCEL
    </Button>,
    <Button
      key="confirm"
      className="large-btn secondary-btn"
      onClick={handleSave}
      disabled={disabled}
      loading={savingRichEditorModal}>
      {btnText}
    </Button>,
  ];

  /**
   * Get the exit modal
   * @returns {React.ReactNode} - The exit modal
   */
  const exitModal = showModal && (
    <Popconfirm
      visible={showModal}
      title={'Do you want to exit?'}
      maskClosable={!savingRichEditorModal}
      closable={false}
      cancelText={'CONTINUE'}
      confirmText={'YES, EXIT'}
      onCancel={() => {
        setShowModal(false);
        setShowPropModal(true);
      }}
      onConfirm={onCancel}
      footer={null}
    />
  );

  /**
   * Render the upload image modal
   * @returns {React.ReactNode} - The upload image modal
   */
  if (selectImage) {
    return (
      <>
        <UploadContentLibraryItem
          showUploadModal={selectImage}
          cancelUploadModal={() => {
            setSelectImage(false);
            // if on cancel needs to go back to galley
            if (cancelModal === 'gallery') {
              return;
            }

            onUploadImageModalClose();
            // close gallery edit modal
            if (!galleryEdit?.v.length && !removeItems) {
              onCancel();
            }
          }}
          type="gallery"
          onSuccess={async (results, activeTab) => {
            if (results && results.length > 0) {
              const stateCopy = [...temporary.images];

              if (temporary?.tempImages?.length) {
                temporary.tempImages.forEach((item) => {
                  const isExist = stateCopy.find((s) => s.i === item.i);
                  if (!isExist) {
                    stateCopy.push(item);
                  }
                });
              }

              const newImages = [];
              for (const result of results) {
                const imageUrl = result.url || result.src;
                if (imageUrl) {
                  try {
                    const { width, height } = await loadImage(imageUrl);
                    newImages.push({
                      i: utils.getRandomString(),
                      w: width,
                      h: height,
                      image: imageUrl,
                    });
                  } catch (err) {
                    // If image fails to load, use default dimensions
                    newImages.push({
                      i: utils.getRandomString(),
                      w: 5,
                      h: 4,
                      image: imageUrl,
                    });
                  }
                }
              }

              if (newImages.length > 0) {
                setTemporary((prev) => ({
                  ...prev,
                  tempImages: [...stateCopy, ...newImages],
                }));

                if (activeTab === 'library' || activeTab === 'ai' || activeTab === 'url') {
                  handleSave();
                }
              }
            }
          }}
          openUploadModal={() => {
            setSelectImage(true);
          }}
          multiSelect={true}
          selectedFolderKey={null}
          prop={prop}
          onAddToGallery={handleSave}
          isWix={isWix}
        />
        {exitModal}
      </>
    );
  }

  /**
   * Render the gallery modal
   * @returns {React.ReactNode} - The gallery modal
   */
  return (
    <>
      <Modal
        {...modalProps}
        wrapClassName={`${modalProps.wrapClassName} gallery-box`}
        title={modalTitle('Edit Gallery')}
        footer={[
          <div
            className={`more-buttons ${moreButtons ? 'active' : 'inactive'}`}
            onClick={() => setMoreButtons(!moreButtons)}>
            <Button className="large-btn light-primary plus-icon" icon={<SectionAddIcon />} />
            <div className="buttons">
              <Button
                className="large-btn light-primary"
                icon={<UploadIcon />}
                onClick={() => {
                  setCancelModal('gallery');
                  setSelectImage(true);
                  setTemporary((prev) => ({ ...prev, tempImages: prev.images }));
                }}>
                Upload Images
              </Button>
            </div>
          </div>,

          <div className="main-buttons">
            {modalFooter({
              onCancel: () => {
                onCancel();
              },
              disabled: savingRichEditorModal,
              btnText: savingRichEditorModal ? 'SAVING' : 'SAVE',
            })}
          </div>,
        ]}>
        <GalleryImageEditor
          {...temporary}
          setTemporary={setTemporary}
          containerWidth={containerWidth}
          onLayoutChange={onLayoutChange}
          onConfigChange={onConfigChange}
        />
      </Modal>
      {exitModal}
    </>
  );
};

GalleryEditModal.defaultProps = {
  savingRichEditorModal: false,
};

GalleryEditModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  galleryEdit: PropTypes.instanceOf(Object).isRequired,
  savingRichEditorModal: PropTypes.bool,
  isWix: PropTypes.bool,
};

export default GalleryEditModal;
